import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import apiEndpointManager from "../../config/index";
import { RootState } from "../store";
import { ProfileState } from "./profileSlice"; // Reuse profile types if possible

// --- Interfaces ---
// Basic connection structure from DynamoDB
interface Connection {
    connectionId: string;
    requesterId: string;
    recipientId: string;
    status: "PENDING" | "ACCEPTED" | "DECLINED";
    createdAt: string;
    updatedAt: string;
}

// Structure for incoming requests (includes requester profile)
export interface EnrichedConnectionRequest extends Connection {
    requesterProfile?: Partial<ProfileState> & { error?: string }; // Add basic profile info
}

// Structure for the status check response
interface StatusBetweenUsers {
    connection: Connection | null;
    loading: boolean;
    error: string | null;
}

// Main state structure for the slice
export interface ConnectionState {
    incomingRequests: {
        data: EnrichedConnectionRequest[];
        loading: boolean;
        error: string | null;
    };
    statusBetweenUsers: StatusBetweenUsers;
    // Track loading/error status per connection update action
    updateStatusLoading: { [connectionId: string]: boolean };
    updateStatusError: { [connectionId: string]: string | null };
}

// --- Initial State ---
const initialState: ConnectionState = {
    incomingRequests: {
        data: [],
        loading: false,
        error: null,
    },
    statusBetweenUsers: {
        connection: null,
        loading: false,
        error: null,
    },
    updateStatusLoading: {},
    updateStatusError: {},
};

// --- Async Thunks ---

// Fetch status between two specific users
export const fetchConnectionStatus = createAsyncThunk(
    "connections/fetchStatus",
    async (
        { userId1, userId2, token }: { userId1: string; userId2: string; token: string },
        { rejectWithValue },
    ) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.get(
                `${endpoint}/connections?userId1=${encodeURIComponent(userId1)}&userId2=${encodeURIComponent(userId2)}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            return response.data; // Expects Connection object or null
        } catch (error: any) {
            console.error("Fetch connection status error:", error.response?.data || error.message);
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch connection status",
            );
        }
    },
);

// Update the status of a specific connection request
export const updateConnectionStatus = createAsyncThunk(
    "connections/updateStatus",
    async (
        {
            connectionId,
            status,
            token,
        }: { connectionId: string; status: "ACCEPTED" | "DECLINED"; token: string },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.put(
                `${endpoint}/connections/${connectionId}`,
                { status }, // Send status in the body
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            // Refetch status between the users involved if the update was successful
            // to keep the statusBetweenUsers state consistent, especially if updated from Profile page
            const state = getState() as RootState;
            const updatedConnection =
                state.connection.incomingRequests.data.find(
                    (req) => req.connectionId === connectionId,
                ) || state.connection.statusBetweenUsers.connection;
            if (updatedConnection) {
                dispatch(
                    fetchConnectionStatus({
                        userId1: updatedConnection.requesterId,
                        userId2: updatedConnection.recipientId,
                        token,
                    }),
                );
            }
            return { connectionId, status, responseData: response.data }; // Return relevant info
        } catch (error: any) {
            console.error("Update connection status error:", error.response?.data || error.message);
            return rejectWithValue({
                connectionId,
                message: error.response?.data?.message || "Failed to update connection status",
            });
        }
    },
);

// Fetch all incoming pending requests for the logged-in user
export const fetchIncomingRequests = createAsyncThunk(
    "connections/fetchIncoming",
    async ({ token }: { token: string }, { rejectWithValue }) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.get(`${endpoint}/connections/requests/incoming`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data as EnrichedConnectionRequest[]; // Expects an array of requests with profile info
        } catch (error: any) {
            console.error("Fetch incoming requests error:", error.response?.data || error.message);
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch incoming requests",
            );
        }
    },
);

// --- Slice Definition ---
const connectionSlice = createSlice({
    name: "connections",
    initialState,
    reducers: {
        // Optional: Reducer to clear state on logout, etc.
        resetConnectionState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // fetchConnectionStatus
            .addCase(fetchConnectionStatus.pending, (state) => {
                state.statusBetweenUsers.loading = true;
                state.statusBetweenUsers.error = null;
            })
            .addCase(
                fetchConnectionStatus.fulfilled,
                (state, action: PayloadAction<Connection | null>) => {
                    state.statusBetweenUsers.loading = false;
                    state.statusBetweenUsers.connection = action.payload;
                },
            )
            .addCase(fetchConnectionStatus.rejected, (state, action) => {
                state.statusBetweenUsers.loading = false;
                state.statusBetweenUsers.error = action.payload as string;
                state.statusBetweenUsers.connection = null; // Clear connection on error
            })

            // updateConnectionStatus
            .addCase(updateConnectionStatus.pending, (state, action) => {
                state.updateStatusLoading[action.meta.arg.connectionId] = true;
                state.updateStatusError[action.meta.arg.connectionId] = null;
            })
            .addCase(updateConnectionStatus.fulfilled, (state, action) => {
                const { connectionId, status } = action.payload;
                state.updateStatusLoading[connectionId] = false;
                // Remove from incoming requests list if accepted/declined
                state.incomingRequests.data = state.incomingRequests.data.filter(
                    (req) => req.connectionId !== connectionId,
                );
                // Update statusBetweenUsers if it matches the updated connection
                if (state.statusBetweenUsers.connection?.connectionId === connectionId) {
                    state.statusBetweenUsers.connection.status = status;
                }
            })
            .addCase(updateConnectionStatus.rejected, (state, action) => {
                const { connectionId, message } = action.payload as {
                    connectionId: string;
                    message: string;
                };
                state.updateStatusLoading[connectionId] = false;
                state.updateStatusError[connectionId] = message;
            })

            // fetchIncomingRequests
            .addCase(fetchIncomingRequests.pending, (state) => {
                state.incomingRequests.loading = true;
                state.incomingRequests.error = null;
            })
            .addCase(
                fetchIncomingRequests.fulfilled,
                (state, action: PayloadAction<EnrichedConnectionRequest[]>) => {
                    state.incomingRequests.loading = false;
                    state.incomingRequests.data = action.payload;
                },
            )
            .addCase(fetchIncomingRequests.rejected, (state, action) => {
                state.incomingRequests.loading = false;
                state.incomingRequests.error = action.payload as string;
            });
    },
});

// --- Exports ---
export const { resetConnectionState } = connectionSlice.actions;
export default connectionSlice.reducer;

// --- Selectors ---
export const selectIncomingRequests = (state: RootState) => state.connection.incomingRequests;
export const selectStatusBetweenUsers = (state: RootState) => state.connection.statusBetweenUsers;
export const selectUpdateConnectionLoading = (state: RootState) =>
    state.connection.updateStatusLoading;
export const selectUpdateConnectionError = (state: RootState) => state.connection.updateStatusError;
